// flow/hooks/useFlowData.ts
import { useMemo, useCallback } from 'react'
import type { Node, Edge } from '@xyflow/react'
import { useProjectPhaseStore } from '@/stores/projectPhaseStore'

interface UseFlowDataProps {
    nodes: Node[]
    edges: Edge[]
    removeNode: (nodeId: string) => void
    onNodesChange: (changes: any) => void
    setPanOnScroll: (pan: boolean) => void
}

export function useFlowData({
    nodes,
    edges,
    removeNode,
    onNodesChange,
    setPanOnScroll,
}: UseFlowDataProps) {
    const { currentPhase, isNodeVisibleInCurrentPhase } = useProjectPhaseStore()

    // Filter visible nodes based on current phase
    const visibleNodes = useMemo(
        () =>
            nodes?.length
                ? nodes.filter((node) =>
                    isNodeVisibleInCurrentPhase(node.type || 'default', node)
                )
                : nodes,
        [nodes, isNodeVisibleInCurrentPhase, currentPhase]
    )

    // Filter valid edges
    const validEdges = useMemo(
        () => edges.filter(
            (edge) =>
                edge &&
                typeof edge === 'object' &&
                'id' in edge &&
                'source' in edge &&
                'target' in edge &&
                typeof edge.source === 'string' &&
                typeof edge.target === 'string'
        ),
        [edges]
    )

    // Enhanced delete function that handles group nodes
    const deleteNodeWithGroups = useCallback((nodeId: string) => {
        // Find the node in the current nodes array
        const nodeToDelete = nodes.find(n => n.id === nodeId)
        if (nodeToDelete && nodeToDelete.type === 'groupnode') {
            // Find all child nodes that have this node as parent
            const childNodes = nodes.filter(node => node.parentId === nodeId)
            console.log('Deleting group node:', nodeId, 'with', childNodes.length, 'children:', childNodes.map(n => n.id))

            // Delete all child nodes first
            childNodes.forEach(childNode => {
                removeNode(childNode.id)
            })
        }

        // Delete the node itself
        removeNode(nodeId)
    }, [nodes, removeNode])

    // Enhanced nodes change handler
    const handleNodesChange = useCallback(
        (props) => {
            if (props[0]?.type === 'select' && props[0]?.selected) {
                setPanOnScroll(true)
            } else if (props[0]?.type === 'select' && !props[0]?.selected) {
                setPanOnScroll(false)
            }
            onNodesChange(props)
        },
        [setPanOnScroll, onNodesChange]
    )

    return {
        visibleNodes,
        validEdges,
        deleteNodeWithGroups,
        handleNodesChange,
    }
}
